import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { Loader2, CheckCircle2, AlertTriangle, Mail, Lock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { claimInvitation } from "@/lib/portal/portal.functions";

export const Route = createFileRoute("/portal/claim/$token")({
  ssr: false,
  head: () => ({ meta: [{ title: "Claim your portal — Michael's Painting" }, { name: "robots", content: "noindex,nofollow" }] }),
  component: ClaimRoute,
});

function ClaimRoute() {
  const { token } = useParams({ from: "/portal/claim/$token" });
  const navigate = useNavigate();
  const claim = useServerFn(claimInvitation);
  const [phase, setPhase] = useState<"auth" | "claiming" | "done" | "error">("auth");
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<"signup" | "signin">("signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const runClaim = async () => {
    setPhase("claiming");
    try {
      await claim({ data: { token } });
      setPhase("done");
      setTimeout(() => navigate({ to: "/portal/dashboard" }), 1200);
    } catch (e) { setError((e as Error).message); setPhase("error"); }
  };

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) runClaim();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      if (mode === "signup") {
        const { data: signUpData, error: e2 } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: `${window.location.origin}/portal/claim/${token}` },
        });
        if (e2) throw e2;
        if (!signUpData.session) {
          // Email confirmation required — try password sign-in in case confirmation is disabled server-side.
          const { data: signInData } = await supabase.auth.signInWithPassword({ email, password });
          if (!signInData.session) {
            throw new Error("Check your email to confirm your account, then return to this link to finish claiming.");
          }
        }
      } else {
        const { data: signInData, error: e2 } = await supabase.auth.signInWithPassword({ email, password });
        if (e2) throw e2;
        if (!signInData.session) throw new Error("Sign-in failed. Please try again.");
      }
      await runClaim();
    } catch (e2) { setError((e2 as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <section className="min-h-screen bg-[#030303] text-white grid place-items-center px-6 py-20 relative overflow-hidden">
      <div className="pointer-events-none absolute -top-40 -left-40 size-[480px] rounded-full bg-amber-400/10 blur-[120px]" />
      <div className="w-full max-w-md relative">
        <Link to="/portal" className="text-xs text-zinc-500 hover:text-amber-300 tracking-wider uppercase">← Portal home</Link>
        <div className="mt-4 rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-8 lg:p-10">
          {phase === "claiming" && (
            <div className="py-10 text-center"><Loader2 className="size-8 animate-spin text-amber-300 mx-auto" /><p className="mt-4 text-sm text-zinc-400">Linking your project…</p></div>
          )}
          {phase === "done" && (
            <div className="py-10 text-center">
              <CheckCircle2 className="size-10 text-amber-300 mx-auto" />
              <p className="mt-4 font-serif text-2xl text-white">You're in.</p>
              <p className="text-sm text-zinc-400 mt-1">Taking you to your dashboard…</p>
            </div>
          )}
          {phase === "error" && (
            <div className="py-8 text-center">
              <AlertTriangle className="size-8 text-red-400 mx-auto" />
              <p className="mt-4 font-serif text-xl text-white">We couldn't claim this invitation</p>
              <p className="text-sm text-red-300 mt-2">{error}</p>
              <button onClick={() => setPhase("auth")} className="mt-6 text-amber-300 underline text-sm">Try a different email</button>
            </div>
          )}
          {phase === "auth" && (
            <>
              <p className="text-amber-300 text-[10px] tracking-[0.3em] uppercase font-semibold">Claim your portal</p>
              <h1 className="font-serif text-3xl text-white mt-2">{mode === "signup" ? "Create your account" : "Sign in to claim"}</h1>
              <p className="text-sm text-zinc-500 mt-2">Use the email address where you received the invitation.</p>
              <form onSubmit={submit} className="mt-6 space-y-3">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-500" />
                  <input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email"
                    className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-3 py-3 text-[16px] focus:border-amber-300/50 outline-none" />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-500" />
                  <input type="password" autoComplete={mode === "signup" ? "new-password" : "current-password"} required minLength={8}
                    value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (min 8 chars)"
                    className="w-full bg-black/40 border border-white/10 rounded-xl pl-10 pr-3 py-3 text-[16px] focus:border-amber-300/50 outline-none" />
                </div>
                {error && <p className="text-sm text-red-400">{error}</p>}
                <button type="submit" disabled={busy} className="w-full bg-amber-300 text-zinc-950 font-semibold py-3 rounded-full text-sm hover:bg-amber-200 disabled:opacity-40 inline-flex items-center justify-center gap-2">
                  {busy && <Loader2 className="size-4 animate-spin" />}
                  {mode === "signup" ? "Create account & claim" : "Sign in & claim"}
                </button>
                <button type="button" onClick={() => { setMode(mode === "signup" ? "signin" : "signup"); setError(null); }}
                  className="w-full text-xs text-zinc-500 hover:text-amber-300 pt-2">
                  {mode === "signup" ? "Already have an account? Sign in" : "Need to create an account? Sign up"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </section>
  );
}